import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAdAccount } from "@/contexts/AdAccountContext";
import { AdAccount } from "@/types";

interface SyncTypeStatus {
  label: string;
  status: "idle" | "syncing" | "done" | "error";
}

export function useSyncStatus() {
  const { user, isMetaConnected, connectMeta } = useAuth();
  const { selectedAdAccountId } = useAdAccount();
  const queryClient = useQueryClient();

  const [accounts, setAccounts] = useState<AdAccount[]>([]);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [syncTypes, setSyncTypes] = useState<SyncTypeStatus[]>([
    { label: "Ad Accounts & Assets", status: "idle" },
    { label: "Campaigns & Ad Sets", status: "idle" },
    { label: "Catalogs & Product Sets", status: "idle" },
  ]);
  const [isSyncing, setIsSyncing] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchAccounts = useCallback(async () => {
    setIsLoadingAccounts(true);
    const { data, error } = await supabase.from("ad_accounts").select("*").order("created_at", { ascending: false });

    if (!mountedRef.current) return;

    if (error) {
      setAccounts([]);
    } else {
      setAccounts(data || []);
      if (data && data.length > 0 && data[0].created_at) {
        setLastSync(new Date(data[0].created_at).toLocaleString());
      }
    }
    setIsLoadingAccounts(false);
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const updateSyncType = useCallback((index: number, status: SyncTypeStatus["status"]) => {
    setSyncTypes((prev) => prev.map((item, i) => (i === index ? { ...item, status } : item)));
  }, []);

  const triggerManualSync = useCallback(async () => {
    if (!user?.id || !selectedAdAccountId) return;

    setIsSyncing(true);
    setSyncTypes([
      { label: "Ad Accounts & Assets", status: "syncing" },
      { label: "Campaigns & Ad Sets", status: "syncing" },
      { label: "Catalogs & Product Sets", status: "syncing" },
    ]);

    const syncAssets = supabase.functions
      .invoke("n8n-proxy", {
        body: {
          action: "GET_ASSETS",
          payload: { ad_account_id: selectedAdAccountId },
        },
      })
      .then(({ error }) => {
        if (error) throw error;
        if (mountedRef.current) updateSyncType(0, "done");
        queryClient.invalidateQueries({ queryKey: ["cached-facebook-pages", user.id] });
        queryClient.invalidateQueries({ queryKey: ["cached-instagram-accounts", user.id] });
        queryClient.invalidateQueries({ queryKey: ["cached-partnership-instagram", user.id, selectedAdAccountId] });
        queryClient.invalidateQueries({
          queryKey: ["cached-partnership-facebook-pages", user.id, selectedAdAccountId],
        });
      })
      .catch(() => {
        if (mountedRef.current) updateSyncType(0, "error");
      });

    const syncCampaigns = supabase.functions
      .invoke("n8n-proxy", {
        body: {
          action: "GET_CAMPAIGNS",
          payload: { ad_account_id: selectedAdAccountId },
        },
      })
      .then(({ error }) => {
        if (error) throw error;
        if (mountedRef.current) updateSyncType(1, "done");
        queryClient.invalidateQueries({ queryKey: ["cached-campaigns", user.id, selectedAdAccountId] });
        queryClient.invalidateQueries({ queryKey: ["cached-ad-sets", user.id, selectedAdAccountId] });
        queryClient.invalidateQueries({ queryKey: ["ad-sets", selectedAdAccountId] });
      })
      .catch(() => {
        if (mountedRef.current) updateSyncType(1, "error");
      });

    const syncCatalogs = supabase.functions
      .invoke("n8n-proxy", {
        body: {
          action: "GET_CATALOGS",
          payload: { ad_account_id: selectedAdAccountId },
        },
      })
      .then(({ error }) => {
        if (error) throw error;
        if (mountedRef.current) updateSyncType(2, "done");
        queryClient.invalidateQueries({ queryKey: ["cached-catalogs", user.id, selectedAdAccountId] });
        queryClient.invalidateQueries({ queryKey: ["cached-product-sets", user.id, selectedAdAccountId] });
      })
      .catch(() => {
        if (mountedRef.current) updateSyncType(2, "error");
      });

    await Promise.allSettled([syncAssets, syncCampaigns, syncCatalogs]);

    if (mountedRef.current) {
      setIsSyncing(false);
      setLastSync(new Date().toLocaleString());
      fetchAccounts();
    }
  }, [user?.id, selectedAdAccountId, queryClient, updateSyncType, fetchAccounts]);

  return {
    accounts,
    lastSync,
    isLoadingAccounts,
    isMetaConnected,
    connectMeta,
    syncTypes,
    isSyncing,
    triggerManualSync,
    selectedAdAccountId,
    refetchAccounts: fetchAccounts,
  };
}
